import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  MenuItem as MenuItemAction,
  Divider,
  Badge
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Build as MaintenanceIcon,
  Inventory as InventoryIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  TrendingDown as LowStockIcon,
  Security as SafetyIcon,
  Navigation as NavigationIcon,
  Sailing as FishingIcon,
  Sports as RecreationIcon,
  Build as MaintenanceIconCategory,
  Weekend as ComfortIcon,
  Category as OtherIcon
} from '@mui/icons-material';
import { getActiveAccessories, updateAccessoryStatus, deleteAccessory, getAccessoryStats } from '../../../../firebase/services/accessoryService';
import { Accessory } from '../../../../models/types';

const AccessoriesPage: React.FC = () => {
  const navigate = useNavigate();
  const [accessories, setAccessories] = useState<Accessory[]>([]);
  const [filteredAccessories, setFilteredAccessories] = useState<Accessory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Table pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [conditionFilter, setConditionFilter] = useState('all');
  
  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAccessory, setSelectedAccessory] = useState<Accessory | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    rentable: 0,
    needingMaintenance: 0,
    lowStock: 0
  });

  useEffect(() => {
    loadAccessories();
    loadStats();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [accessories, searchTerm, statusFilter, categoryFilter, conditionFilter]);

  const loadAccessories = async () => {
    try {
      setLoading(true);
      const accessoryList = await getActiveAccessories();
      setAccessories(accessoryList);
    } catch (err) {
      console.error('Error loading accessories:', err);
      setError('Failed to load accessories');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await getAccessoryStats();
      setStats({
        total: statsData.total,
        active: statsData.active,
        rentable: statsData.rentable,
        needingMaintenance: statsData.needingMaintenance,
        lowStock: statsData.lowStock
      });
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const applyFilters = () => {
    let filtered = accessories;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(accessory =>
        (accessory.barcode && accessory.barcode.toLowerCase().includes(searchTerm.toLowerCase())) ||
        accessory.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (accessory.brand && accessory.brand.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (accessory.model && accessory.model.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(accessory => accessory.status === statusFilter);
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(accessory => accessory.category === categoryFilter);
    }

    // Condition filter
    if (conditionFilter !== 'all') {
      filtered = filtered.filter(accessory => accessory.condition === conditionFilter);
    }

    setFilteredAccessories(filtered);
  };

  const handleDeleteAccessory = async () => {
    if (!selectedAccessory) return;
    
    try {
      await deleteAccessory(selectedAccessory.id);
      setSuccess('Accessory deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedAccessory(null);
      loadAccessories();
      loadStats();
    } catch (err) {
      console.error('Error deleting accessory:', err);
      setError('Failed to delete accessory');
    }
  };

  const handleStatusChange = async (accessoryId: string, newStatus: 'active' | 'inactive' | 'maintenance' | 'lost' | 'retired') => {
    try {
      await updateAccessoryStatus(accessoryId, newStatus);
      setSuccess(`Accessory status updated to ${newStatus}`);
      loadAccessories();
      loadStats();
    } catch (err) {
      console.error('Error updating accessory status:', err);
      setError('Failed to update accessory status');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'maintenance': return 'warning';
      case 'lost': return 'error';
      case 'retired': return 'secondary';
      default: return 'default';
    }
  };

  const getConditionColor = (condition: string) => {
    switch (condition) {
      case 'excellent': return 'success';
      case 'good': return 'primary';
      case 'fair': return 'warning';
      case 'poor': return 'error';
      case 'out-of-service': return 'secondary';
      default: return 'default';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'safety': return <SafetyIcon fontSize="small" />;
      case 'navigation': return <NavigationIcon fontSize="small" />;
      case 'fishing': return <FishingIcon fontSize="small" />;
      case 'recreation': return <RecreationIcon fontSize="small" />;
      case 'maintenance': return <MaintenanceIconCategory fontSize="small" />;
      case 'comfort': return <ComfortIcon fontSize="small" />;
      default: return <OtherIcon fontSize="small" />;
    }
  };

  const getAccessoryAlerts = (accessory: Accessory) => {
    const alerts = [];
    const now = new Date();

    // Check maintenance due
    if (accessory.nextMaintenanceDate && new Date(accessory.nextMaintenanceDate) <= now) {
      alerts.push('maintenance');
    }

    // Check low stock
    const threshold = Math.max(1, Math.floor(accessory.quantity * 0.2));
    if (accessory.availableQuantity <= threshold) {
      alerts.push('lowStock');
    }

    return alerts;
  };

  const handleActionMenuClick = (event: React.MouseEvent<HTMLElement>, accessory: Accessory) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedAccessory(accessory);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setSelectedAccessory(null);
  };

  // Get unique values for filters
  const uniqueCategories = [...new Set(accessories.map(a => a.category))];
  const uniqueConditions = [...new Set(accessories.map(a => a.condition))];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Accessories & Safety Equipment
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/equipment/accessories/new')}
        >
          Add Accessory
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary">{stats.total}</Typography>
              <Typography variant="body2" color="text.secondary">Total Items</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">{stats.active}</Typography>
              <Typography variant="body2" color="text.secondary">Active</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="info.main">{stats.rentable}</Typography>
              <Typography variant="body2" color="text.secondary">Rentable</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Badge badgeContent={stats.needingMaintenance} color="warning">
                <MaintenanceIcon color="warning" fontSize="large" />
              </Badge>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Maintenance Due
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Badge badgeContent={stats.lowStock} color="error">
                <LowStockIcon color="error" fontSize="large" />
              </Badge>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Low Stock
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Search by barcode, type, brand, model..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
                <MenuItem value="lost">Lost</MenuItem>
                <MenuItem value="retired">Retired</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                <MenuItem value="all">All Categories</MenuItem>
                {uniqueCategories.map(category => (
                  <MenuItem key={category} value={category}>{category}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Condition</InputLabel>
              <Select value={conditionFilter} onChange={(e) => setConditionFilter(e.target.value)}>
                <MenuItem value="all">All Conditions</MenuItem>
                {uniqueConditions.map(condition => (
                  <MenuItem key={condition} value={condition}>{condition}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setCategoryFilter('all');
                  setConditionFilter('all');
                }}
              >
                Clear Filters
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Accessories Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Barcode</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Available</TableCell>
                <TableCell>Condition</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Alerts</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAccessories
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((accessory) => {
                  const alerts = getAccessoryAlerts(accessory);
                  return (
                    <TableRow key={accessory.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getCategoryIcon(accessory.category)}
                          <Box sx={{ ml: 1 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {accessory.barcode || 'No Barcode'}
                            </Typography>
                            {accessory.brand && (
                              <Typography variant="caption" color="text.secondary">
                                {accessory.brand} {accessory.model}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={accessory.category}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{accessory.type}</TableCell>
                      <TableCell>{accessory.quantity}</TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {accessory.availableQuantity}
                          {accessory.reservedQuantity && (
                            <Typography component="span" variant="caption" color="text.secondary">
                              {' '}({accessory.reservedQuantity} reserved)
                            </Typography>
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={accessory.condition}
                          color={getConditionColor(accessory.condition) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={accessory.status}
                          color={getStatusColor(accessory.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          {alerts.includes('maintenance') && (
                            <Tooltip title="Maintenance Due">
                              <MaintenanceIcon color="warning" fontSize="small" />
                            </Tooltip>
                          )}
                          {alerts.includes('lowStock') && (
                            <Tooltip title="Low Stock">
                              <LowStockIcon color="error" fontSize="small" />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(e) => handleActionMenuClick(e, accessory)}
                          size="small"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredAccessories.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
      >
        <MenuItemAction onClick={() => {
          navigate(`/admin/equipment/accessories/view/${selectedAccessory?.id}`);
          handleActionMenuClose();
        }}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItemAction>
        <MenuItemAction onClick={() => {
          navigate(`/admin/equipment/accessories/edit/${selectedAccessory?.id}`);
          handleActionMenuClose();
        }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItemAction>
        <Divider />
        <MenuItemAction onClick={() => {
          if (selectedAccessory) handleStatusChange(selectedAccessory.id, 'active');
          handleActionMenuClose();
        }}>
          <ListItemIcon><CheckCircleIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Set Active</ListItemText>
        </MenuItemAction>
        <MenuItemAction onClick={() => {
          if (selectedAccessory) handleStatusChange(selectedAccessory.id, 'maintenance');
          handleActionMenuClose();
        }}>
          <ListItemIcon><MaintenanceIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Set Maintenance</ListItemText>
        </MenuItemAction>
        <MenuItemAction onClick={() => {
          if (selectedAccessory) handleStatusChange(selectedAccessory.id, 'inactive');
          handleActionMenuClose();
        }}>
          <ListItemIcon><WarningIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Set Inactive</ListItemText>
        </MenuItemAction>
        <Divider />
        <MenuItemAction onClick={() => {
          setDeleteDialogOpen(true);
          handleActionMenuClose();
        }}>
          <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Retire</ListItemText>
        </MenuItemAction>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Retire Accessory</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to retire accessory with barcode "{selectedAccessory?.barcode || selectedAccessory?.type}"? This will mark it as retired and remove it from active inventory.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteAccessory} color="error" variant="contained">
            Retire
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccessoriesPage; 